import { memo, useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import _ from 'lodash'
import { itemHelper, projectHelper, userHelper } from '../../common/src/helpers'
import { AMPLITUDE_ACTION_TYPES, dispatchEvent } from '../../common/src/eventTracking/amplitudeEvents'
import { useOrganizedUsersOptions } from '../../hooks/useUsersOption'
import { UsersPanel } from '../users/panels/UsersPanel'
import { inviteGroupToItemWithId, inviteToItemWithId, uninviteToItemWithId } from '../../common/src/actions/itemsAPI'
import { useUsersMenuProps } from '../../hooks/usersMenuPropsHooks'
import { useTranslation } from 'react-i18next'
import { useToastController } from '@fluentui/react-components'
import { FluentToast } from '../toast/FluentToast'

export const FollowersPanel = memo(({ serverID, project, item, onDismiss, ...rest }) => {
  const dispatch = useDispatch()
  const { dispatchToast } = useToastController()
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const itemFollowers = itemHelper.getFollowers(item, false)

  const followers = useMemo(() => {
    return itemFollowers
      ? itemFollowers
          .sortBy(follower => {
            return userHelper.getEmail(follower)
          })
          .toArray()
      : []
  }, [itemFollowers])

  const options = useOrganizedUsersOptions({
    project,
    minusUsers: followers,
  })

  const onReceiveFollowers = useCallback(
    apiAction => {
      if (apiAction.error) {
        const email = apiAction.meta.email
        // Extract error message from API response
        const errorResponse = apiAction.payload.response || apiAction.payload
        let errorMessage = t('item_detail.followers_invite_error', { email })
        
        // Check if there's a specific error message from the API
        if (errorResponse.error_message) {
          // Handle error_message field first
          errorMessage = errorResponse.error_message
        } else if (errorResponse.error) {
          if (Array.isArray(errorResponse.error)) {
            // Handle array format like ["Only project administrators can add followers to items in restricted projects."]
            errorMessage = errorResponse.error[0] || errorMessage
          } else if (typeof errorResponse.error === 'string') {
            try {
              // Try to parse as JSON in case it's a stringified array
              const parsed = JSON.parse(errorResponse.error)
              if (Array.isArray(parsed)) {
                errorMessage = parsed[0] || errorMessage
              } else {
                errorMessage = errorResponse.error
              }
            } catch {
              // If parsing fails, use the string as-is
              errorMessage = errorResponse.error
            }
          }
        } else if (errorResponse.message) {
          // Handle message format
          errorMessage = errorResponse.message
        }
        
        //show toast
        dispatchToast(<FluentToast>{errorMessage}</FluentToast>, {
          timeout: -1,
          intent: 'error',
        })
      }
      setLoading(false)
    },
    [dispatchToast, t]
  )

  const onSelectFollower = useCallback(
    user => {
      const email = user ? userHelper.getEmail(user) : ''
      if (_.isEmpty(email)) {
        return
      }

      setLoading(true)
      dispatch(inviteToItemWithId(serverID, email)).then(onReceiveFollowers)
      dispatch(dispatchEvent(AMPLITUDE_ACTION_TYPES.ADD_FOLLOWER))
    },
    [dispatch, onReceiveFollowers, serverID]
  )

  const addGroupToItem = useCallback(
    async group => {
      setLoading(true)
      try {
        const response = await dispatch(inviteGroupToItemWithId(serverID, group.id))
        
        // Handle error response similar to individual follower invitation
        if (response.error) {
          const errorResponse = response.payload.response || response.payload
          let errorMessage = t('item_detail.followers_invite_error', { email: group.name || 'group' })
          
          // Check if there's a specific error message from the API
          if (errorResponse.error_message) {
            // Handle error_message field first
            errorMessage = errorResponse.error_message
          } else if (errorResponse.error) {
            if (Array.isArray(errorResponse.error)) {
              // Handle array format like ["Only project administrators can add followers to items in restricted projects."]
              errorMessage = errorResponse.error[0] || errorMessage
            } else if (typeof errorResponse.error === 'string') {
              try {
                // Try to parse as JSON in case it's a stringified array
                const parsed = JSON.parse(errorResponse.error)
                if (Array.isArray(parsed)) {
                  errorMessage = parsed[0] || errorMessage
                } else {
                  errorMessage = errorResponse.error
                }
              } catch {
                // If parsing fails, use the string as-is
                errorMessage = errorResponse.error
              }
            }
          } else if (errorResponse.message) {
            // Handle message format
            errorMessage = errorResponse.message
          }
          
          //show toast
          dispatchToast(<FluentToast>{errorMessage}</FluentToast>, {
            timeout: -1,
            intent: 'error',
          })
        }
      } catch (error) {
        // Handle any unexpected errors
        dispatchToast(<FluentToast>{t('item_detail.followers_invite_error', { email: group.name || 'group' })}</FluentToast>, {
          timeout: -1,
          intent: 'error',
        })
      }
      setLoading(false)

      dispatch(dispatchEvent(AMPLITUDE_ACTION_TYPES.ADD_FOLLOWER_GROUP))
    },
    [dispatch, serverID, t, dispatchToast]
  )

  const onSelectGroup = useCallback(
    /**
     * @typedef {import("@/types/userGroup").UserGroup} UserGroup
     * @param {UserGroup} group
     */
    async group => {
      const emails = group.memberships.map(membership => membership.user.email)

      if (_.isEmpty(emails)) return

      addGroupToItem(group)
    },
    [addGroupToItem]
  )

  const onRemoveFollower = useCallback(
    user => {
      const email = user ? userHelper.getEmail(user) : ''
      if (_.isEmpty(email)) {
        return
      }
      setLoading(true)

      dispatch(uninviteToItemWithId(serverID, email)).then(onReceiveFollowers)
      dispatch(dispatchEvent(AMPLITUDE_ACTION_TYPES.REMOVE_FOLLOWER))
    },
    [dispatch, onReceiveFollowers, serverID]
  )

  const menuProps = useUsersMenuProps({ users: followers, object: item })
  const invitationIconProps = useMemo(() => ({ title: t('item_detail.add_follower_tooltip') }), [t])

  // Check if project is restricted - hide external access for all users on items
  const isProjectRestricted = projectHelper.isRestricted(project)
  const shouldHideExternalAccess = isProjectRestricted

  return (
    <UsersPanel
      headerText={t('item_detail.followers_panel_header')}
      onDismiss={onDismiss}
      users={followers}
      onSelectGroup={onSelectGroup}
      options={options}
      onSelectUser={onSelectFollower}
      onRemoveUser={onRemoveFollower}
      usersHeader={t('users.followed_by')}
      textFieldPlaceholder={t('item_detail.followers_panel_text_field_placeholder')}
      invitationIconProps={invitationIconProps}
      loading={loading}
      menuProps={menuProps}
      removeText={t('users.press_to_remove')}
      hideEmailInput={shouldHideExternalAccess}
      hideUserGroups={shouldHideExternalAccess}
      {...rest}
    />
  )
})
